import type {
  ApprovedSearchResult,
  PendingApprovalEntry,
} from '@clockwise/clients/timesheet-approvals';
import { DatePicker } from '@clockwise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import { ArrowDown, ArrowUp, ChevronsUpDown, Loader2, Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { timesheetApprovalsApi, timesheetsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { EntityPicker } from './entity-picker';
import { customerPickerConfig, userPickerConfig } from './entity-pickers';
import {
  DisabledActionReason,
  LOCKED_ENTRY_REJECT_TOOLTIP,
  RejectReasonPopover,
} from './reject-reason-popover';
import { BillableBadge } from './timesheet-billable';
import { formatHours, shortDate } from './timesheet-week';

interface ApprovedSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Concrete filters captured when the user hits Search (so the list only
 *  reloads on an explicit search, not on every filter keystroke). */
interface SearchParams {
  fromDate: string;
  toDate: string;
  customerIds: string[];
  userIds: string[];
}

// Server hard-caps a page at 500; beyond that we ask the user to narrow.
const RESULT_LIMIT = 500;
const ISO = 'yyyy-MM-dd';

/** Fresh default state each time the dialog opens — the current month, no
 *  entity filters, no prior results. */
function defaultDates(): { fromDate: string; toDate: string } {
  const now = new Date();
  return {
    fromDate: format(startOfMonth(now), ISO),
    toDate: format(endOfMonth(now), ISO),
  };
}

/**
 * "Search approved time" dialog, opened from the approvals page header
 * kebab. Finds entries the approver ALREADY approved (server-scoped to
 * their own decisions) so they can reject one after the fact. Reject
 * reuses the shared reason popover and fires the same rejected email +
 * resubmission flow as a first-time reject.
 */
export function ApprovedSearchDialog({ open, onOpenChange }: ApprovedSearchDialogProps) {
  const queryClient = useQueryClient();
  const [customerIds, setCustomerIds] = useState<string[]>([]);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [params, setParams] = useState<SearchParams | null>(null);

  // Every open starts from a clean slate — reset filters + results so the
  // dialog never shows a previous search.
  useEffect(() => {
    if (!open) return;
    const d = defaultDates();
    setCustomerIds([]);
    setUserIds([]);
    setFromDate(d.fromDate);
    setToDate(d.toDate);
    setParams(null);
  }, [open]);

  const canSearch = fromDate !== '' && toDate !== '' && fromDate <= toDate;

  const queryKey = params ? queryKeys.timesheetApprovals.approvedSearch(params) : ['noop'];
  const searchQuery = useQuery<ApprovedSearchResult>({
    queryKey,
    queryFn: () =>
      timesheetApprovalsApi.searchApproved({
        fromDate: params?.fromDate,
        toDate: params?.toDate,
        customerIds: params?.customerIds.length ? params.customerIds : undefined,
        userIds: params?.userIds.length ? params.userIds : undefined,
        limit: RESULT_LIMIT,
      }),
    enabled: open && params !== null,
    // A search is an explicit, correctness-sensitive action: the per-row
    // `locked` flag gates the Reject button, so it must reflect the server
    // right now — never a cached result from a prior open (e.g. rows cached
    // as unlocked before an operator enabled period locking would otherwise
    // show Reject enabled until a background refetch corrected it). Drop the
    // cache the moment the dialog closes and always refetch on search.
    staleTime: 0,
    gcTime: 0,
  });

  const rejectMutation = useMutation({
    mutationFn: (input: { timesheetId: string; reason: string }) =>
      timesheetsApi.rejectApproved(input.timesheetId, input.reason),
    onSuccess: (_res, input) => {
      // The row flip is applied asynchronously by the approval-event consumer,
      // so optimistically drop it from the current result set.
      if (params) {
        queryClient.setQueryData<ApprovedSearchResult>(
          queryKeys.timesheetApprovals.approvedSearch(params),
          (prev) =>
            prev
              ? {
                  ...prev,
                  data: prev.data.filter((e) => e.timesheetId !== input.timesheetId),
                  meta: { ...prev.meta, total: Math.max(0, prev.meta.total - 1) },
                }
              : prev,
        );
      }
      // The rejected entry re-enters the employee's queue on resubmit; refresh
      // the pending list + sidebar count. Do NOT invalidate the whole
      // `timesheetApprovals` subtree — that includes THIS `approvedSearch`
      // query and would refetch it, undoing the optimistic removal above (the
      // row flip is async, so the entry is still `approved` server-side and
      // would pop back into the results).
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.timesheetApprovals.all, 'pending'],
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.timesheetApprovals.count() });
      toast.success('Entry rejected. The employee has been notified to resubmit.');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Reject failed');
    },
  });

  const runSearch = () => {
    if (!canSearch) return;
    setParams({
      fromDate,
      toDate,
      customerIds: [...customerIds].sort(),
      userIds: [...userIds].sort(),
    });
  };

  const entries = searchQuery.data?.data ?? [];
  const total = searchQuery.data?.meta.total ?? 0;
  const truncated = total > RESULT_LIMIT;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Search approved time</DialogTitle>
          <DialogDescription>
            Find entries you approved and reject one to send it back for changes.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {/* Customer and employee share a row (each half width) to keep the
              filters compact and leave more vertical room for results. Badges
              still wrap and grow the box's height on multi-select. */}
          <div className="flex items-start gap-3">
            <div className="grid flex-1 gap-1.5">
              <Label htmlFor="approved-search-customer" className="text-xs text-muted-foreground">
                Customer
              </Label>
              <EntityPicker
                multiple
                id="approved-search-customer"
                config={customerPickerConfig}
                value={customerIds}
                onChange={setCustomerIds}
                placeholder="All customers"
                searchPlaceholder="Search customers…"
                loadStrategy="preload"
                preloadLimit={500}
                badgeOverflow="wrap"
              />
            </div>
            <div className="grid flex-1 gap-1.5">
              <Label htmlFor="approved-search-employee" className="text-xs text-muted-foreground">
                Employee
              </Label>
              <EntityPicker
                multiple
                id="approved-search-employee"
                config={userPickerConfig}
                value={userIds}
                onChange={setUserIds}
                placeholder="All employees"
                searchPlaceholder="Search employees…"
                loadStrategy="preload"
                preloadLimit={500}
                badgeOverflow="wrap"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="approved-search-from" className="text-xs text-muted-foreground">
                From
              </Label>
              <DatePicker
                id="approved-search-from"
                value={fromDate}
                onChange={setFromDate}
                className="w-40"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="approved-search-to" className="text-xs text-muted-foreground">
                To
              </Label>
              <DatePicker
                id="approved-search-to"
                value={toDate}
                onChange={setToDate}
                className="w-40"
              />
            </div>
          </div>
          <div>
            <Button size="sm" onClick={runSearch} disabled={!canSearch || searchQuery.isFetching}>
              {searchQuery.isFetching ? <Loader2 className="animate-spin" /> : <Search />}
              Search
            </Button>
          </div>
        </div>

        <div className="mt-2 max-h-[55vh] overflow-y-auto pr-2">
          {params === null ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Set your filters and search to find approved entries.
            </p>
          ) : searchQuery.isError ? (
            <p className="py-8 text-center text-sm text-destructive">
              Couldn't load approved entries. Try again.
            </p>
          ) : searchQuery.isPending ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Searching…</p>
          ) : entries.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No approved entries match these filters.
            </p>
          ) : (
            <ApprovedResultsTable
              entries={entries}
              onReject={(timesheetId, reason) => rejectMutation.mutate({ timesheetId, reason })}
              rejectingId={
                rejectMutation.isPending ? (rejectMutation.variables?.timesheetId ?? null) : null
              }
            />
          )}
        </div>

        {params !== null && !searchQuery.isPending && entries.length > 0 ? (
          <p className="text-xs text-muted-foreground">
            {truncated
              ? `Showing the first ${RESULT_LIMIT} of ${total} — narrow your search (customer, employee, or a shorter date range) to see the rest.`
              : `Showing ${entries.length} of ${total} entr${total === 1 ? 'y' : 'ies'} you approved.`}
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

// Fixed date/hours/billable/reject columns; the flexible columns (employee,
// customer, activity, description) share the remaining width, with the
// free-text description given the most room.
const ROW_GRID =
  'grid grid-cols-[80px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.4fr)_60px_max-content_96px] items-start gap-2';

// Every column is sortable except the free-text description and the reject
// action. `dir` is the direction applied when a column first becomes active.
type SortKey = 'date' | 'employee' | 'customer' | 'activity' | 'hours' | 'billable';

function compareBy(a: PendingApprovalEntry, b: PendingApprovalEntry, key: SortKey): number {
  switch (key) {
    case 'date':
      return a.date.localeCompare(b.date);
    case 'employee':
      return `${a.userLastName} ${a.userFirstName}`.localeCompare(
        `${b.userLastName} ${b.userFirstName}`,
      );
    case 'customer':
      return a.customerName.localeCompare(b.customerName);
    case 'activity':
      return (a.productName ?? '').localeCompare(b.productName ?? '');
    case 'hours':
      return Number(a.hours) - Number(b.hours);
    case 'billable':
      return Number(a.billable) - Number(b.billable);
  }
}

function ApprovedResultsTable({
  entries,
  onReject,
  rejectingId,
}: {
  entries: PendingApprovalEntry[];
  onReject: (timesheetId: string, reason: string) => void;
  /** Timesheet id whose reject is currently in flight, or null. */
  rejectingId: string | null;
}) {
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' } | null>(null);

  // Clicking a header sorts ascending, then toggles to descending, then clears
  // back to the original (server) order on the third click.
  const toggleSort = (key: SortKey) => {
    setSort((prev) => {
      if (prev?.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };

  const sorted = sort
    ? [...entries].sort((a, b) => {
        const cmp = compareBy(a, b, sort.key);
        return sort.dir === 'asc' ? cmp : -cmp;
      })
    : entries;

  return (
    <div className="divide-y">
      <div
        className={cn(
          ROW_GRID,
          'px-1 pb-2 text-[11px] uppercase tracking-wide text-muted-foreground',
        )}
      >
        <SortHeader label="Date" sortKey="date" sort={sort} onSort={toggleSort} />
        <SortHeader label="Employee" sortKey="employee" sort={sort} onSort={toggleSort} />
        <SortHeader label="Customer" sortKey="customer" sort={sort} onSort={toggleSort} />
        <SortHeader label="Activity" sortKey="activity" sort={sort} onSort={toggleSort} />
        <span>Description</span>
        <SortHeader label="Hours" sortKey="hours" sort={sort} onSort={toggleSort} align="right" />
        <SortHeader label="Billable" sortKey="billable" sort={sort} onSort={toggleSort} />
        <span />
      </div>
      {sorted.map((e) => (
        <div key={e.timesheetId} className={cn(ROW_GRID, 'px-1 py-2 text-sm')}>
          <span className="text-muted-foreground tabular-nums">{shortDate(e.date)}</span>
          <span className="break-words">{`${e.userFirstName} ${e.userLastName}`}</span>
          <span className="break-words">{e.customerName}</span>
          <span className="break-words text-muted-foreground">{e.productName ?? '—'}</span>
          <span className="break-words text-muted-foreground">{e.description || '—'}</span>
          <span className="text-right tabular-nums">{formatHours(Number(e.hours))}</span>
          <BillableBadge billable={e.billable} />
          <div className="flex justify-end">
            {rejectingId === e.timesheetId ? (
              <Button
                size="sm"
                variant="outline"
                disabled
                className="border-destructive/40 text-destructive"
              >
                <Loader2 className="size-3.5 animate-spin" />
                Rejecting…
              </Button>
            ) : e.locked === false ? (
              // Disabled by default — Reject is enabled ONLY when the server
              // confirms the entry is not period-locked (`locked === false`).
              // Any other state (locked, or an unexpected missing flag) stays
              // disabled so a stale/uncertain value can never allow a reject.
              <RejectReasonPopover
                title="Reject approved entry"
                description="The employee will be notified to fix and resubmit this entry."
                onConfirm={(reason) => onReject(e.timesheetId, reason)}
                trigger={
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={rejectingId !== null}
                    className="border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
                  >
                    <X className="size-3.5" />
                    Reject
                  </Button>
                }
              />
            ) : (
              <DisabledActionReason title={LOCKED_ENTRY_REJECT_TOOLTIP}>
                <Button
                  size="sm"
                  variant="outline"
                  disabled
                  className="border-destructive/40 text-destructive"
                >
                  <X className="size-3.5" />
                  Reject
                </Button>
              </DisabledActionReason>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: SortKey;
  sort: { key: SortKey; dir: 'asc' | 'desc' } | null;
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right' | 'center';
}) {
  const active = sort?.key === sortKey;
  const Icon = !active ? ChevronsUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={cn(
        'flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-foreground',
        active && 'text-foreground',
        align === 'right' && 'justify-end',
        align === 'center' && 'justify-center',
      )}
      aria-label={`Sort by ${label}`}
    >
      {label}
      <Icon className={cn('size-3', !active && 'opacity-40')} aria-hidden />
    </button>
  );
}
